
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar, Search, Table, Grid } from "lucide-react";
import { ProviderBooking } from "@/hooks/useProviderBookings";
import BookingsTable from "./BookingsTable";
import BookingsCalendar from "./BookingsCalendar";
import { useIsMobile } from "@/hooks/use-mobile";

interface BookingsManagementProps {
  bookings: ProviderBooking[];
  bookingsLoading: boolean;
  bookingsError: any;
  onViewDetails: (bookingId: string) => void;
  onStatusUpdate: (bookingId: string, status: string) => void;
}

const BookingsManagement = ({ 
  bookings, 
  bookingsLoading, 
  bookingsError, 
  onViewDetails, 
  onStatusUpdate 
}: BookingsManagementProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const isMobile = useIsMobile();

  useEffect(() => {
    console.log('BookingsManagement - bookings:', bookings?.length || 0);
    console.log('BookingsManagement - loading:', bookingsLoading);
    console.log('BookingsManagement - error:', bookingsError);
  }, [bookings, bookingsLoading, bookingsError]);

  const filteredBookings = bookings?.filter(booking => {
    const matchesSearch = searchQuery === "" || 
      booking.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = selectedStatus === "all" || booking.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  }) || [];

  const todayBookings = filteredBookings.filter(booking => {
    const today = new Date().toISOString().split('T')[0];
    return booking.booking_date === today;
  });

  const upcomingBookings = filteredBookings.filter(booking => {
    const today = new Date().toISOString().split('T')[0];
    return booking.booking_date > today;
  }).sort((a, b) => {
    // Sort upcoming bookings in ascending order (closest date first)
    if (a.booking_date === b.booking_date) {
      return a.start_time.localeCompare(b.start_time);
    }
    return a.booking_date.localeCompare(b.booking_date);
  });

  const pastBookings = filteredBookings.filter(booking => {
    const today = new Date().toISOString().split('T')[0];
    return booking.booking_date < today;
  }).sort((a, b) => {
    // Sort past bookings in descending order (newest first)
    if (a.booking_date === b.booking_date) {
      return b.start_time.localeCompare(a.start_time);
    }
    return b.booking_date.localeCompare(a.booking_date);
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col space-y-4">
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Bookings Management</span>
          </CardTitle>
          
          <div className="flex flex-col space-y-3">
            {/* View Mode Toggle */}
            <div className="flex items-center justify-center sm:justify-start">
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <Button
                  variant={viewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  className="h-8 px-3 text-xs sm:text-sm"
                >
                  <Table className="h-4 w-4 mr-1" />
                  Table
                </Button>
                <Button
                  variant={viewMode === "calendar" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("calendar")}
                  className="h-8 px-3 text-xs sm:text-sm"
                >
                  <Grid className="h-4 w-4 mr-1" />
                  Calendar
                </Button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by customer name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
              >
                <option value="all">All Bookings</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={isMobile ? "px-3" : ""}>
        {bookingsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading bookings...</p>
            </div>
          </div>
        ) : bookingsError ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="font-medium">Error loading bookings</p>
              <p className="text-sm text-red-500 mt-1">{bookingsError.message}</p>
            </div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            {bookings && bookings.length > 0 ? (
              <div>
                <p className="font-medium">No bookings match your filters</p>
                <p className="text-sm mt-2">Try adjusting your search or status filter</p>
              </div>
            ) : (
              <div>
                <p className="font-medium">No bookings yet</p>
                <p className="text-sm mt-2">Bookings will appear here once customers start booking</p>
              </div>
            )}
          </div>
        ) : viewMode === "calendar" ? (
          <BookingsCalendar 
            bookings={filteredBookings}
            onViewDetails={onViewDetails}
          />
        ) : (
          <div className="space-y-6">
            <BookingsTable 
              bookings={todayBookings}
              title="Today's Bookings"
              onViewDetails={onViewDetails}
              onStatusUpdate={onStatusUpdate}
            />
            <BookingsTable 
              bookings={upcomingBookings}
              title="Upcoming Bookings"
              onViewDetails={onViewDetails}
              onStatusUpdate={onStatusUpdate}
            />
            <BookingsTable 
              bookings={pastBookings}
              title="Past Bookings"
              onViewDetails={onViewDetails}
              onStatusUpdate={onStatusUpdate}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BookingsManagement;
